const ENTERPRISE_ORIGIN_HOST = 'enterprise.perplexity.ai';
const MS_OFFICE_ADDIN_PATH_PREFIX = '/ms-office-addin/';
// enterprise.perplexity.ai is a prod-only host; redirecting there from a non-prod origin
// (localhost, staging, pplx.dev, testing) only breaks the session — the dev session can
// never complete on the prod host. The backend `enterprise-origin-enabled` Eppo flag is
// frequently wide-open in non-prod envs, so this client guard is what keeps dev sign-in
// working regardless of how the flag resolves. Mirrors getDeployName()'s prod definition in
// platform-components/env.ts; we can't import it here because platform-components depends on
// auth, which would make this a circular dependency.
const PROD_ORIGIN_HOSTS = new Set(['www.perplexity.ai', 'www.perplexity.com']);
/**
 * If the backend signaled (via the enterprise-origin-enabled
 * Eppo flag) that the user should be funneled to enterprise.perplexity.ai before
 * sign-in, redirect there now (preserving email + callbackUrl). Returns true if
 * the redirect fired and the caller should bail out of its sign-in flow.
 *
 * Only fires in production (www.perplexity.ai / www.perplexity.com). The target host is
 * hardcoded to prod, so a redirect from any non-prod origin (localhost, staging, etc.) would
 * just break the session — see PROD_ORIGIN_HOSTS above.
 *
 * Skips when we're already on the enterprise origin (would otherwise loop).
 *
 * Only call from email-driven sign-in paths (SSO, magic-link). Google/Apple
 * OAuth flows that don't collect an email up front have nothing to look up.
 *
 * Uses window.location.assign with a static https://enterprise.perplexity.ai
 * URL — no need for sanitizedRedirectWindow's protocol allowlist since the
 * target host is hardcoded. Keeps platform-core bundle size minimal.
 */
export function maybeRedirectToEnterpriseOrigin({ shouldRedirect, email, callbackUrl, }) {
    if (!shouldRedirect)
        return false;
    if (typeof window === 'undefined')
        return false;
    if (!PROD_ORIGIN_HOSTS.has(window.location.hostname))
        return false;
    if (window.location.hostname === ENTERPRISE_ORIGIN_HOST)
        return false;
    // MS Office add-in popup auth handshake is same-origin only (see authClient.ts
    // awaitPopupLogin / AuthCallbackPage). Same rationale as isWindowsApp / isCometIOS bypass.
    if (callbackUrl.startsWith(MS_OFFICE_ADDIN_PATH_PREFIX))
        return false;
    const params = new URLSearchParams({ email, callbackUrl });
    window.location.assign(`https://${ENTERPRISE_ORIGIN_HOST}/auth/signin?${params.toString()}`);
    return true;
}
//# sourceMappingURL=redirectToEnterpriseOrigin.js.map